import { useCallback, useEffect, useRef } from 'react';

import { Disclaimer } from '@/components/common';
import { DISCLAIMER_FLAG_KEY } from '@/constant/localStorage';
import {
  DISCLAIMER_ACCEPTED_EVENT,
  DISCLAIMER_MODAL_ID,
} from '@/constant/modals';
import { useModal } from '@/hooks/useModal';
import { useUser } from '@/store/auth';
import { useCompany } from '@/store/company';
import storage from '@/utils/storage';

export const useDisclaimer = () => {
  const { user, isLoading } = useUser();
  const { data: company, isLoading: isCompanyLoading } = useCompany();
  const { openModal, closeModal } = useModal();
  const hasShownDisclaimer = useRef(false);

  const hideDisclaimer = useCallback(() => {
    storage.setItem(DISCLAIMER_FLAG_KEY, 'true');
    closeModal(DISCLAIMER_MODAL_ID);
    window.dispatchEvent(new Event(DISCLAIMER_ACCEPTED_EVENT));
  }, [closeModal]);

  const showDisclaimer = useCallback(() => {
    if (hasShownDisclaimer.current) return;

    hasShownDisclaimer.current = true;
    openModal({
      id: DISCLAIMER_MODAL_ID,
      title: '',
      body: <Disclaimer onAccept={hideDisclaimer} />,
      size: 'md',
      hideCloseButton: true,
      hideCustomCloseButton: true,
      isCentered: true,
      closeOnEsc: false,
      closeOnOverlayClick: false,
      headerProps: {
        display: 'none',
      },
      contentProps: {
        rounded: '16px',
        overflow: 'hidden',
        sx: {
          '& > div': {
            p: 0,
          },
        },
      },
      overlayProps: {
        bg: 'blackAlpha.500',
      },
    });
  }, [openModal, hideDisclaimer]);

  useEffect(() => {
    const hasDisclaimerBeenAccepted = storage.getItem(DISCLAIMER_FLAG_KEY);
    const isBigChangeSetupRequired =
      Boolean(user?.company_id) &&
      (isCompanyLoading || !company || !company.bc_client_id);

    if (
      isLoading ||
      !user ||
      hasDisclaimerBeenAccepted ||
      isBigChangeSetupRequired
    ) {
      return;
    }

    showDisclaimer();
  }, [company, isCompanyLoading, isLoading, user, showDisclaimer]);
};
